import React, { useEffect, useMemo, useState } from "react";
import { getPortfolioData } from "../../data/portfolioData";
import "./Contact.css"
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa";
import { FiMail, FiPhone, FiGlobe, FiMapPin, FiRefreshCw, FiShield } from "react-icons/fi";

const WA_NUMBER = "918883521093"; // without + sign

const Contact = () => {
  const { contact } = getPortfolioData();

  // form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  // simple math captcha
  const [challenge, setChallenge] = useState({ a: 0, b: 0 });
  const [answer, setAnswer] = useState("");
  const correct = useMemo(() => challenge.a + challenge.b, [challenge]);
  const isHuman = Number(answer) === correct;

  const [errors, setErrors] = useState({});

  useEffect(() => {
    regenerate();
  }, []);

  const regenerate = () => {
    // 2–9 so it's readable
    const a = Math.floor(Math.random() * 8) + 2;
    const b = Math.floor(Math.random() * 8) + 2;
    setChallenge({ a, b });
    setAnswer("");
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email.";
    if (!form.subject.trim()) next.subject = "Please enter a subject.";
    if (!form.message.trim()) next.message = "Please write a message.";
    if (!isHuman) next.captcha = "Solve the math to verify you are human.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Build line-by-line WhatsApp message
    const now = new Date();
    const lines = [
      "*New Contact Enquiry*",
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Subject: ${form.subject}`,
      "Message:",
      form.message,
      "—",
      `Sent at: ${now.toLocaleString()}`,
      `Source: ${contact?.website || "Portfolio"}`
    ];
    const text = encodeURIComponent(lines.join("\n"));

    const waURL = `https://wa.me/${WA_NUMBER}?text=${text}`;
    window.open(waURL, "_blank");
  };

  const isDisabled =
    !form.name || !form.email || !form.subject || !form.message || !isHuman;

  return (
    <section className="contact-section">
        <h2 className="section-title">Contact Me</h2>
      <div className="contact-container">
        {/* Left Info */}
        <div className="contact-info">
          <h2 className="title">Let’s get in touch</h2>
          <p className="subtitle">
            We’re open for any suggestion or just to have a chat
          </p>

          <ul>
            <li>
              <FiMapPin className="icon" />
              <span>{contact.address}</span>
            </li>
            <li>
              <FiPhone className="icon" />
              <span>{contact.phone}</span>
            </li>
            <li>
              <FiMail className="icon" />
              <span>{contact.email}</span>
            </li>
            <li>
              <FiGlobe className="icon" />
              <span>{contact.website}</span>
            </li>
          </ul>

          <div className="social-icons">
              <a href={contact.instagram} target="_blank" rel="noreferrer">
                <FaInstagram />
              </a>
              <a href={contact.whatsapp} target="_blank" rel="noreferrer">
                <FaWhatsapp />
              </a>
              <a href={contact.linkedIn} target="_blank" rel="noreferrer">
                <FaLinkedinIn />
              </a>          
              </div>
        </div>

        {/* Right Form */}
        <div className="contact-form">
          <h2 className="title">Get in touch</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className={`form-group ${errors.name ? "has-error" : ""}`}>
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={onChange}
                />
                {errors.name && <span className="error">{errors.name}</span>}
              </div>
              <div className={`form-group ${errors.email ? "has-error" : ""}`}>
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={form.email}
                  onChange={onChange}
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </div>
            </div>

            <div className={`form-group ${errors.subject ? "has-error" : ""}`}>
              <label>Subject</label>
              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={form.subject}
                onChange={onChange}
              />
              {errors.subject && <span className="error">{errors.subject}</span>}
            </div>

            <div className={`form-group ${errors.message ? "has-error" : ""}`}>
              <label>Message</label>
              <textarea
                name="message"
                placeholder="Write your message..."
                value={form.message}
                onChange={onChange}
              />
              {errors.message && <span className="error">{errors.message}</span>}
            </div>

            {/* Human / Robot verification */}
            <div className={`captcha ${errors.captcha ? "has-error" : ""}`}>
              <div className="captcha-prompt">
                <FiShield />
                <span>Verify you’re human</span>
              </div>
              <div className="captcha-row">
                <span className="math">
                  {challenge.a} + {challenge.b} = 
                </span>
                <input
                  className="captcha-input"
                  inputMode="numeric"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ""))}
                  placeholder="?"
                  aria-label="Captcha answer"
                />
                <button
                  type="button"
                  className="refresh"
                  onClick={regenerate}
                  title="New challenge"
                >
                  <FiRefreshCw />
                </button>
              </div>
              {!isHuman && errors.captcha && (
                <span className="error">{errors.captcha}</span>
              )}
            </div>

            <button type="submit" className="btn" disabled={isDisabled}>
              Send on WhatsApp
            </button>
            <p className="helper">
              Clicking send will open WhatsApp with your message pre-filled.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
